'use client';

import type React from 'react';
import { loginWithRedirect, useFetchUser } from '@/auth/hooks';
import { Button } from '@components/components/ui/button';

interface AuthBlurGateProps {
  children: React.ReactNode;
  heading?: string;
  description?: string;
  buttonLabel?: string;
}

/**
 * Gate component that renders children with a blur overlay and login CTA
 * when the user is not authenticated. Acts as a teaser to nudge sign-in.
 */
export const AuthBlurGate: React.FC<AuthBlurGateProps> = ({
  children,
  heading = 'Try Chargebee APIs and inspect responses',
  description = 'Login to send API requests in the playground. Your Chargebee test site will be used automatically.',
  buttonLabel = 'Login to continue',
}) => {
  const { isLoggedIn, loading } = useFetchUser();

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-50px)] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-shade-teal-500" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="relative h-[calc(100vh-50px)] overflow-hidden">
        {/* Blurred content teaser */}
        <div className="pointer-events-none select-none blur-sm" aria-hidden>
          {children}
        </div>

        {/* Login CTA overlay */}
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/10">
          <div className="flex flex-col items-center max-w-lg px-8 py-10 rounded-2xl bg-white/80 backdrop-blur-xl shadow-[0px_4px_19px_rgba(0,0,0,0.12)] border border-white/60">
            <h3 className="text-lg font-semibold text-gray-900 text-center">
              {heading}
            </h3>

            <p className="text-sm text-gray-500 mt-3 text-center leading-relaxed">
              {description}
            </p>

            <Button
              onClick={loginWithRedirect}
              size="lg"
              className="mt-6 rounded-full bg-shade-teal-500 px-8 text-white hover:bg-shade-teal-600 hover:scale-[1.02] transition-all"
            >
              {buttonLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
